import type { FC, FormEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../../../../../store/store';
import { setUser } from '../../../../../../store/AuthSlice';
import { getCsrfToken } from '../../../../../../utils/helpers';
import type { UserProfile } from './popups/userData.types';
import styles from './EmailConfirmPanel.module.scss';

type Props = {
    user: UserProfile;
};
type Phase = 'idle' | 'sending' | 'code' | 'confirming' | 'confirmed' | 'error';

const RESEND_COOLDOWN_MS = 5 * 60 * 1000;

const nowMs = () => Date.now();
const normEmailKey = (email: string) =>
    String(email || '')
        .trim()
        .toLowerCase();
const keyLastSent = (email: string) => `email_confirm_last_sent_at:${normEmailKey(email)}`;

function safeReadLastSentAt(email: string): number | null {
    try {
        const raw = localStorage.getItem(keyLastSent(email));
        if (!raw) return null;
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
        return null;
    }
}

function safeWriteLastSentAt(email: string, ts: number) {
    try {
        localStorage.setItem(keyLastSent(email), String(ts));
    } catch {
        // ignore
    }
}

function calcRemainingMs(lastSentAt: number | null, now: number): number {
    if (!lastSentAt) return 0;
    const left = RESEND_COOLDOWN_MS - (now - lastSentAt);
    return left > 0 ? left : 0;
}

function fmtMMSS(ms: number): string {
    const totalSec = Math.ceil(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const EmailConfirmPanel: FC<Props> = ({ user }) => {
    const dispatch = useDispatch<AppDispatch>();
    const email = String(user?.email || '').trim();
    const serverConfirmed = Boolean(user?.emailStatus);
    const [expanded, setExpanded] = useState(false);
    const [phase, setPhase] = useState<Phase>(serverConfirmed ? 'confirmed' : 'idle');
    const [code, setCode] = useState('');
    const [message, setMessage] = useState('');
    const [lastSentAt, setLastSentAt] = useState<number | null>(() => (email ? safeReadLastSentAt(email) : null));
    const [nowTick, setNowTick] = useState(() => nowMs());
    const abortRef = useRef<AbortController | null>(null);
    const tickRef = useRef<number | null>(null);
    const emailConfirmed = serverConfirmed || phase === 'confirmed';
    const leftMs = useMemo(() => calcRemainingMs(lastSentAt, nowTick), [lastSentAt, nowTick]);
    const canResend = leftMs <= 0;

    useEffect(() => {
        abortRef.current?.abort();
        setCode('');
        setMessage('');
        if (!email) {
            setPhase('idle');
            setLastSentAt(null);
            setNowTick(nowMs());
            return;
        }
        if (serverConfirmed) {
            setPhase('confirmed');
            setExpanded(false);
        } else {
            setPhase((p) => (p === 'confirmed' ? 'idle' : p));
        }
        setLastSentAt(safeReadLastSentAt(email));
        setNowTick(nowMs());
    }, [email, serverConfirmed]);

    useEffect(() => {
        const shouldTick = expanded && phase === 'code' && email && calcRemainingMs(lastSentAt, nowMs()) > 0;
        if (!shouldTick) {
            if (tickRef.current) {
                window.clearInterval(tickRef.current);
                tickRef.current = null;
            }
            return;
        }
        if (!tickRef.current) {
            tickRef.current = window.setInterval(() => {
                setNowTick(nowMs());
            }, 1000);
        }
        return () => {
            if (tickRef.current) {
                window.clearInterval(tickRef.current);
                tickRef.current = null;
            }
        };
    }, [expanded, phase, email, lastSentAt]);

    useEffect(() => {
        return () => {
            abortRef.current?.abort();
            if (tickRef.current) {
                window.clearInterval(tickRef.current);
                tickRef.current = null;
            }
        };
    }, []);

    const requestCode = useCallback(async () => {
        if (!email) {
            setPhase('error');
            setMessage('Сначала укажите email в личных данных.');
            setExpanded(true);
            return;
        }
        if (emailConfirmed) return;
        if (phase === 'code' && !canResend) return;
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setPhase('sending');
        setMessage('');
        try {
            const csrf = await getCsrfToken();
            const r = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/request-email-code`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'csrf-token': csrf },
                signal: ctrl.signal,
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                setPhase('error');
                setMessage(data?.message || 'Не удалось отправить код подтверждения.');
                return;
            }
            const ts = nowMs();
            safeWriteLastSentAt(email, ts);
            setLastSentAt(ts);
            setNowTick(ts);
            setPhase('code');
            setMessage('Код отправлен. Проверьте почту и введите его ниже.');
        } catch (e: any) {
            if (e?.name === 'AbortError') return;
            setPhase('error');
            setMessage('Ошибка сети при отправке кода.');
        }
    }, [email, emailConfirmed, phase, canResend]);

    const openAndSend = useCallback(async () => {
        if (emailConfirmed) return;
        setExpanded(true);
        await requestCode();
    }, [emailConfirmed, requestCode]);

    const submitCode = useCallback(
        async (e: FormEvent) => {
            e.preventDefault();
            if (!email) {
                setPhase('error');
                setMessage('Сначала укажите email в личных данных.');
                return;
            }
            if (emailConfirmed) return;
            const v = code.trim();
            if (!v) {
                setPhase('error');
                setMessage('Введите код подтверждения.');
                return;
            }
            abortRef.current?.abort();
            const ctrl = new AbortController();
            abortRef.current = ctrl;
            setPhase('confirming');
            setMessage('');
            try {
                const csrf = await getCsrfToken();
                const r = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/confirm-email`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json', 'csrf-token': csrf },
                    body: JSON.stringify({ code: v, email }),
                    signal: ctrl.signal,
                });
                const data = await r.json().catch(() => ({}));
                if (!r.ok) {
                    setPhase('error');
                    setMessage(data?.message || 'Ошибка подтверждения.');
                    return;
                }
                dispatch(setUser(data));
                setPhase('confirmed');
                setMessage('Email подтверждён.');
                setCode('');
                setExpanded(false);
            } catch (err: any) {
                if (err?.name === 'AbortError') return;
                setPhase('error');
                setMessage('Ошибка сети при подтверждении.');
            }
        },
        [code, email, emailConfirmed, dispatch],
    );

    const closePanel = useCallback(() => {
        abortRef.current?.abort();
        setExpanded(false);
        setMessage('');
        setCode('');
        setPhase(serverConfirmed ? 'confirmed' : 'idle');
        setNowTick(nowMs());
    }, [serverConfirmed]);

    const busy = phase === 'sending' || phase === 'confirming';

    if (!expanded) {
        return (
            <div className={styles.collapsed} aria-label="Подтверждение электронной почты">
                <button
                    className={styles.confirmBtn}
                    type="button"
                    onClick={openAndSend}
                    disabled={busy || emailConfirmed || !email}
                    title={!email ? 'Сначала укажите email в личных данных' : emailConfirmed ? 'Email уже подтверждён' : undefined}
                >
                    {emailConfirmed ? 'Почта подтверждена' : phase === 'sending' ? 'Отправляем…' : 'Подтвердить почту'}
                </button>
            </div>
        );
    }

    return (
        <div className={styles.expanded} aria-label="Подтверждение электронной почты">
            <div className={styles.topRow}>
                <div className={styles.titleBlock}>
                    <div className={styles.title}>Подтверждение почты</div>
                    {/* {email ? <div className={styles.email}>{email}</div> : null} */}
                </div>
                <button className={styles.close} type="button" onClick={closePanel} disabled={busy} aria-label="Закрыть">
                    ✕
                </button>
            </div>
            {emailConfirmed ? (
                <div className={`${styles.msg} ${styles.ok}`}>Email подтверждён ✅</div>
            ) : message ? (
                <div className={`${styles.msg} ${phase === 'error' ? styles.err : styles.ok}`}>{message}</div>
            ) : (
                <div className={styles.hint}>Введите код из письма.</div>
            )}
            <form className={styles.form} onSubmit={submitCode}>
                <div className={styles.row}>
                    <input
                        className={styles.input}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="Код"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        disabled={busy || emailConfirmed}
                    />
                    <button className={styles.btn} type="submit" disabled={busy || emailConfirmed || !email}>
                        {phase === 'confirming' ? 'Проверяем…' : 'Подтвердить'}
                    </button>
                </div>
                <div className={styles.bottomRow}>
                    <button className={styles.link} type="button" onClick={requestCode} disabled={busy || emailConfirmed || !email || !canResend}>
                        {canResend ? (
                            'Отправить код ещё раз'
                        ) : (
                            <>
                                Повторно через <span className={styles.countdown}>{fmtMMSS(leftMs)}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EmailConfirmPanel;
